"use client";

import Image from "next/image";
import axios, { isAxiosError } from "axios";
import { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { KeyRound, Link2 } from "lucide-react";
import { useRouter } from "next/navigation";

import { BaseDialogFooter } from "@/components/Modals/BaseDialog";
import { ConfigFormRenderer } from "@/components/Form/ConfigFormRenderer";
import { Skeleton } from "@/components/shadcn/skeleton";
import { SpotToast } from "@/components/SpotToast";
import { DATA_KEYS } from "@/utils/useRemoteData";

import { DeviceAuthConnect } from "./DeviceAuthConnect";
import { OAuthAppForm } from "./OAuthAppForm";
import type { IntegrationInfo, ProviderInfo } from "./types";
import {
  getAdminOAuthSetupRequiredDetails,
  hasOAuthAppForm,
  isOAuthAppComplete,
  oauthStartErrorMessage,
  startOAuthFlow,
  type AdminOAuthSetupRequiredDetails,
} from "./utils";

export function IntegrationConnectForm({
  integration,
  groupId,
  returnTo,
  onBack,
  onSuccess,
  onAdminSetupRequired,
}: {
  integration: IntegrationInfo;
  groupId: string;
  returnTo: string;
  onBack: () => void;
  onSuccess: () => void;
  onAdminSetupRequired: (details: AdminOAuthSetupRequiredDetails) => void;
}) {
  const router = useRouter();
  const queryClient = useQueryClient();

  const availableProviders = useMemo(
    () => integration.providers.filter((p) => p.available),
    [integration.providers]
  );

  const needsPicker = availableProviders.length > 1;
  const autoProvider = !needsPicker ? (availableProviders[0] ?? null) : null;

  const [selectedProvider, setSelectedProvider] = useState<ProviderInfo | null>(
    null
  );
  const [oauthStarting, setOauthStarting] = useState(false);

  const activeProvider = selectedProvider ?? autoProvider;
  const showPicker = needsPicker && selectedProvider === null;

  const configEndpoint = activeProvider?.config_url ?? "";
  const submitEndpoint = activeProvider?.save_url ?? "";
  const isSecretProvider = activeProvider?.auth_type === "secret_auth";
  const isDeviceProvider = activeProvider?.auth_type === "device_code";
  const isOAuthAppProvider =
    !!activeProvider && hasOAuthAppForm(activeProvider);

  const {
    data: configData,
    isLoading: configLoading,
    error: configError,
  } = useQuery({
    queryKey: ["integration-config", integration.type, configEndpoint],
    queryFn: async () => {
      const response = await axios.post(`/api/backend${configEndpoint}`, {});
      return response.data;
    },
    enabled:
      !showPicker &&
      !!configEndpoint &&
      (isSecretProvider || isOAuthAppProvider),
  });

  const initialValues = useMemo(() => {
    const values: Record<string, unknown> = {};
    if (configData?.config) {
      Object.keys(configData.config).forEach((key) => {
        const cfg = configData.config[key] as { default?: unknown } | undefined;
        values[key] = cfg?.default ?? "";
      });
    }
    return values;
  }, [configData]);

  const form = useForm({ defaultValues: initialValues, mode: "onChange" });

  useEffect(() => {
    if (configData?.config) form.reset(initialValues);
  }, [configData, form, initialValues]);

  const formValues = useWatch({ control: form.control });
  const oauthAppComplete =
    !!configData?.config && isOAuthAppComplete(configData.config, formValues);

  const submitMutation = useMutation({
    mutationFn: async (formData: Record<string, unknown>) => {
      return axios.post(`/api/backend${submitEndpoint}`, formData);
    },
    onSuccess: () => {
      SpotToast.success(`${integration.display_name} integrated successfully.`);
      queryClient.invalidateQueries({ queryKey: [DATA_KEYS.INTEGRATIONS] });
      queryClient.invalidateQueries({
        queryKey: [DATA_KEYS.INTEGRATIONS_CONFIG],
      });
      onSuccess();
    },
    onError: (error: unknown) => {
      const message = isAxiosError(error)
        ? ((error.response?.data as { message?: string } | undefined)
            ?.message ?? error.message)
        : error instanceof Error
          ? error.message
          : "Unknown error";
      SpotToast.error(
        `Failed to integrate ${integration.display_name}: ${message}`
      );
    },
  });

  const startOAuth = (
    provider: ProviderInfo,
    connectionConfig?: Record<string, unknown>
  ) => {
    setOauthStarting(true);
    startOAuthFlow(
      groupId,
      integration,
      provider,
      returnTo,
      undefined,
      connectionConfig
    )
      .then((url) => router.push(url))
      .catch((err) => {
        setOauthStarting(false);
        const adminDetails = getAdminOAuthSetupRequiredDetails(err);
        if (adminDetails) {
          onAdminSetupRequired(adminDetails);
          return;
        }
        SpotToast.error(oauthStartErrorMessage(err));
      });
  };

  const handleProviderClick = (provider: ProviderInfo) => {
    if (
      provider.auth_type === "secret_auth" ||
      provider.auth_type === "device_code" ||
      hasOAuthAppForm(provider)
    ) {
      setSelectedProvider(provider);
      return;
    }
    startOAuth(provider);
  };

  const handleSubmit = () => {
    submitMutation.mutate(form.getValues());
  };

  const handleBack = () => {
    if (needsPicker && selectedProvider) {
      setSelectedProvider(null);
      return;
    }
    onBack();
  };

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="flex-1 min-h-0 overflow-y-auto thin-scrollbars px-6 py-4">
        <div className="flex items-start gap-3 pb-4 mb-4 border-b border-border">
          <div className="shrink-0 w-10 h-10 rounded-lg bg-background border border-border/40 flex items-center justify-center p-1.5">
            <Image
              width={28}
              height={28}
              alt={integration.display_name}
              src={integration.image}
              style={{ objectFit: "contain", width: "100%", height: "100%" }}
            />
          </div>
          <div className="flex flex-col min-w-0">
            <span className="font-medium text-foreground">
              {integration.display_name}
            </span>
            <span className="text-sm text-muted-foreground">
              {showPicker
                ? "Choose an authentication method"
                : isSecretProvider
                  ? `Configure your connection to ${integration.display_name} below.`
                  : isOAuthAppProvider
                    ? `Enter your OAuth app, then sign in to ${integration.display_name}.`
                    : isDeviceProvider
                      ? (activeProvider?.display_name ??
                        `Sign in to ${integration.display_name}.`)
                      : `Connect your ${integration.display_name} account.`}
            </span>
          </div>
        </div>

        {showPicker ? (
          <div className="flex flex-col gap-2">
            {availableProviders.map((provider) => (
              <button
                key={provider.key}
                type="button"
                disabled={oauthStarting}
                onClick={() => handleProviderClick(provider)}
                className="flex items-center gap-3 p-3 pr-4 rounded-lg border border-border bg-card shadow-xs cursor-pointer hover:border-ring transition-colors text-left disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <div className="shrink-0 w-9 h-9 rounded-lg bg-background border border-border/40 flex items-center justify-center p-1.5">
                  {provider.image ? (
                    <Image
                      width={24}
                      height={24}
                      alt={provider.display_name}
                      src={provider.image}
                      style={{
                        objectFit: "contain",
                        width: "100%",
                        height: "100%",
                      }}
                    />
                  ) : integration.image ? (
                    <Image
                      width={24}
                      height={24}
                      alt={integration.display_name}
                      src={integration.image}
                      style={{
                        objectFit: "contain",
                        width: "100%",
                        height: "100%",
                      }}
                    />
                  ) : provider.auth_type === "secret_auth" ? (
                    <KeyRound size={20} className="text-muted-foreground" />
                  ) : (
                    <Link2 size={20} className="text-muted-foreground" />
                  )}
                </div>
                <div className="flex flex-col flex-1 min-w-0">
                  <span className="font-medium text-sm text-foreground truncate">
                    {provider.display_name}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {provider.auth_type === "secret_auth"
                      ? "Connect using API credentials"
                      : provider.auth_type === "device_code"
                        ? "Sign in with a one-time code"
                        : `Sign in with your ${integration.display_name} account with OAuth`}
                  </span>
                </div>
              </button>
            ))}
          </div>
        ) : isDeviceProvider && activeProvider ? (
          <DeviceAuthConnect
            integrationType={integration.type}
            displayName={integration.display_name}
            provider={activeProvider}
            onSuccess={onSuccess}
          />
        ) : !isSecretProvider && !isOAuthAppProvider ? (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-muted-foreground">
              You&apos;ll be redirected to {integration.display_name} to
              authorize access.
            </p>
          </div>
        ) : configLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : configError ? (
          <p className="text-sm text-destructive">
            Failed to load configuration. Please try again.
          </p>
        ) : configData?.config && isOAuthAppProvider && activeProvider ? (
          <OAuthAppForm
            provider={activeProvider}
            integrationName={integration.display_name}
            config={configData.config}
            form={form}
          />
        ) : configData?.config ? (
          <ConfigFormRenderer
            config={configData.config}
            form={form}
            getNodeById={() => undefined}
          />
        ) : (
          <p className="text-sm text-muted-foreground">
            No configuration required.
          </p>
        )}
      </div>

      <BaseDialogFooter
        footerActions={
          showPicker
            ? [
                {
                  label: "Back",
                  props: {
                    variant: "outline",
                    onClick: handleBack,
                    disabled: oauthStarting,
                  },
                },
              ]
            : isDeviceProvider
              ? [
                  {
                    label: needsPicker && selectedProvider ? "Back" : "Cancel",
                    props: { variant: "outline", onClick: handleBack },
                  },
                ]
              : isSecretProvider
                ? [
                    {
                      label:
                        needsPicker && selectedProvider ? "Back" : "Cancel",
                      props: { variant: "outline", onClick: handleBack },
                    },
                    {
                      label: "Connect",
                      props: {
                        onClick: handleSubmit,
                        loading: submitMutation.status === "pending",
                        disabled: configLoading || !!configError,
                      },
                    },
                  ]
                : [
                    {
                      label:
                        needsPicker && selectedProvider ? "Back" : "Cancel",
                      props: {
                        variant: "outline",
                        onClick: handleBack,
                        disabled: oauthStarting,
                      },
                    },
                    {
                      label: "Continue with OAuth",
                      props: {
                        onClick: () => {
                          if (!activeProvider) return;
                          if (isOAuthAppProvider) {
                            startOAuth(activeProvider, form.getValues());
                            return;
                          }
                          handleProviderClick(activeProvider);
                        },
                        loading: oauthStarting,
                        disabled:
                          !activeProvider ||
                          (isOAuthAppProvider &&
                            (configLoading ||
                              !!configError ||
                              !oauthAppComplete)),
                      },
                    },
                  ]
        }
      />
    </div>
  );
}
